import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import { orderBy } from "lodash";
import { useFormikContext } from "formik";
import getBillingLabel from "../../../utils/billing/getBillingLabel";

const styles = {
  headerCell: {
    fontWeight: "semibold",
    fontSize: 16,
    py: "8px",
  },
  cell: {
    fontSize: 16,
    py: "4px",
  },
  returnIcon: {
    mr: 1,
    transform: "rotate(180deg) scaleY(-1)",
  },
  inputContainer: {
    display: "flex",
    justifyContent: "flex-end",
  },
};

export default function EditableFieldsTable({
  title,
  baseName,
  fieldsObject,
  displayHeader = true,
  readOnly = false,
  fieldOrder = null,
}) {
  const { values, setFieldValue } = useFormikContext();

  if (!fieldsObject) return null;

  // Get ordered fields based on fieldOrder or fall back to alphabetical
  const getOrderedFields = () => {
    if (fieldOrder && Array.isArray(fieldOrder)) {
      // Use custom order, but only include fields that actually exist
      const orderedFields = fieldOrder.filter(key => fieldsObject.hasOwnProperty(key));
      // Add any remaining fields that weren't in the order list
      const remainingFields = Object.keys(fieldsObject).filter(key => !fieldOrder.includes(key));
      return [...orderedFields, ...remainingFields];
    }
    // Fall back to alphabetical ordering
    return orderBy(
      Object.entries(fieldsObject),
      [([key, value]) => (getBillingLabel(key, value) || "").toLowerCase()]
    ).map(([key]) => key);
  };

  const handleChange = key => event => {
    const value = event.target.value;
    // Convert to number to prevent storing strings in the database
    // Empty string remains empty, otherwise parse as float
    const numericValue = value === '' ? '' : parseFloat(value) || 0;
    const path = `${baseName}.${key}`;
    setFieldValue(path, numericValue);
  };

  const getValue = key => {
    const pathParts = baseName.split(".");
    let current = values;
    for (const part of pathParts) {
      current = current?.[part];
    }
    return current?.[key] ?? "";
  };

  return (
    <>
      {displayHeader && (
        <TableHead>
          <TableRow>
            <TableCell sx={styles.headerCell}>{title}</TableCell>
            <TableCell sx={{ ...styles.cell, textAlign: "right" }}>
              {/* Add New functionality removed due to missing components */}
            </TableCell>
          </TableRow>
        </TableHead>
      )}
      <TableBody>
        {getOrderedFields().map(
          key => (
            <TableRow key={key}>
              <TableCell sx={styles.cell}>
                <Box display="flex" alignItems="center">
                  <KeyboardReturnIcon htmlColor="#d5d5d5" sx={styles.returnIcon} />
                  <Typography fontSize={16}>{getBillingLabel(key, fieldsObject[key])}</Typography>
                </Box>
              </TableCell>
              <TableCell sx={styles.cell} align="right">
                <Box sx={styles.inputContainer}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" color="text.secondary">
                      $
                    </Typography>
                    <TextField
                      size="small"
                      type="number"
                      name={`${baseName}.${key}`}
                      value={getValue(key)}
                      onChange={handleChange(key)}
                      disabled={readOnly}
                      sx={{ 
                        width: 80,
                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                          "-webkit-appearance": "none",
                          margin: 0
                        },
                        "& input[type=number]": {
                          "-moz-appearance": "textfield"
                        }
                      }}
                    />
                  </Box>
                </Box>
              </TableCell>
            </TableRow>
          )
        )}
      </TableBody>
    </>
  );
}
